import React, { useState, useRef } from 'react';
import { Modal, Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import { manualUploadAudio } from '../services/api';

const ManualUploadModal = ({ show, onHide, videoId, narrator, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/webm', 'audio/ogg', 'audio/x-wav'];
      if (!allowedTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.type}. Allowed: mp3, wav, webm, ogg`);
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      await manualUploadAudio(videoId, narrator, selectedFile, (progress) => {
        setUploadProgress(progress);
      });
      
      // Success
      setUploadProgress(100);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      handleClose();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop={isUploading ? 'static' : true}>
      <Modal.Header closeButton={!isUploading}>
        <Modal.Title>Manual Upload - {narrator}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form.Group className="mb-3">
          <Form.Label>Select Audio File</Form.Label>
          <Form.Control
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
            disabled={isUploading}
            ref={fileInputRef}
          />
          <Form.Text className="text-muted">
            Supported formats: MP3, WAV, WebM, OGG
          </Form.Text>
        </Form.Group>

        {selectedFile && (
          <div className="mb-3">
            <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}

        {isUploading && (
          <ProgressBar 
            now={uploadProgress} 
            label={`${uploadProgress}%`} 
            animated 
            striped 
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload} 
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManualUploadModal;
